import { useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { flushSync } from 'react-dom';
import { TranslationService } from '../../services/translation.service';
import ValidationSummary from '../Shared/ValidationSummary';
import TextFieldFormGroup, { type ITextFieldFormGroupState } from '../Shared/TextFieldFormGroup';
import ValidationInfo from '../../validation/validation-info';
import type IValidatable from '../Shared/validatable.interface';
import FileUpload, { type IFileUploadState } from '../Shared/FileUpload';
import SubmitSuccessful from '../Shared/SubmitSuccessful';
import SubmitFailed from '../Shared/SubmitFailed';
import ContactInformationLookup, { type IContactInformationLookupState } from '../Shared/ContactInformationLookup';
import { ContactInformationLookupField } from '../Shared/ContactInformationLookup/contact-information-lookup-field.enum';
import Consent, { type IConsentState } from '../Shared/Consent';
import HoneyPot, { type IHoneyPot } from '../Shared/HoneyPot';
import { ValidatableComponent, ValidationHelper } from '../../helpers/validation.helper';
import type { IServerValidationError } from '../Shared/validatable.interface';
import type IReclamationApplication from './models/reclamation-application.model';
import { CompensationType } from '../../models/compensation-type.enum';
import { LanguageService } from '../../services/language.service';
import CompensationTypeSelector, { type ICompensationTypeSelectorState } from '../Shared/CompensationTypeSelector';
import { AnalyticsService } from '../../services/analytics-service';
import ActualTripSelector, { type IActualTripSelectorState } from '../Shared/ActualTripSelector';
import { DateHelper } from '../../helpers/date.helper';
import { Status } from '../../hooks/fetch.hook';
import { emptyContactInformation } from '../../models/contact-information.model';
import { ActualTripSelectorMode } from '../Shared/ActualTripSelector/actual-trip-selector-mode.enum';
import { ActualTripSelectorField } from '../Shared/ActualTripSelector/actual-trip-selector-field.enum';

function Reclamation() {
  // === STATE ===
  const [descriptionState, setDescriptionState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [showActualTrip, setShowActualTrip] = useState(false);
  const [actualTripSelectorState, setActualTripSelectorState] = useState<IActualTripSelectorState>({
    value: {
      fromLocation: undefined,
      fromDateTime: DateHelper.roundToNearestFiveMinutes(DateTime.now().minus({ hours: 2 })),
      toLocation: undefined,
      toDateTime: DateHelper.roundToNearestFiveMinutes(DateTime.now().minus({ hours: 2 })),
      comments: '',
      mode: ActualTripSelectorMode.edit,
    },
    validationInfo: new ValidationInfo(),
  });
  const [fileUploadState, setFileUploadState] = useState<IFileUploadState>({ value: [], validationInfo: new ValidationInfo() });
  const [contactInformationLookupState, setContactInformationLookupState] = useState<IContactInformationLookupState>({
    value: emptyContactInformation,
    validationInfo: new ValidationInfo(),
  });
  const [compensationTypeState, setCompensationTypeState] = useState<ICompensationTypeSelectorState>({ value: CompensationType.ToGoVoucherByEmail, validationInfo: new ValidationInfo() });
  const [consentState, setConsentState] = useState<IConsentState>({ value: false, validationInfo: new ValidationInfo() });
  const [submitStatus, setSubmitStatus] = useState(Status.notperformed);
  const [submitErrorCode, setSubmitErrorCode] = useState<number | undefined>(undefined);

  // === REFS ===
  const descriptionRef = useRef<IValidatable>(null!);
  const actualTripSelectorRef = useRef<IValidatable>(null!);
  const fileUploadRef = useRef<IValidatable>(null!);
  const contactInformationLookupRef = useRef<IValidatable>(null!);
  const compensationTypeRef = useRef<IValidatable>(null!);
  const consentRef = useRef<IValidatable>(null!);
  const honeyPotRef = useRef<IHoneyPot>(null!);

  // === HELPER FUNCTIONS ===
  function getValidatableComponents(): ValidatableComponent[] {
    const result: ValidatableComponent[] = [];
    result.push({ component: descriptionRef.current, validationInfo: descriptionState.validationInfo, setState: setDescriptionState });
    if (showActualTrip) {
      result.push({ component: actualTripSelectorRef.current, validationInfo: actualTripSelectorState.validationInfo, setState: setActualTripSelectorState });
    }
    return result.concat([
      { component: fileUploadRef.current, validationInfo: fileUploadState.validationInfo, setState: setFileUploadState },
      { component: contactInformationLookupRef.current, validationInfo: contactInformationLookupState.validationInfo, setState: setContactInformationLookupState },
      { component: consentRef.current, validationInfo: consentState.validationInfo, setState: setConsentState },
      { component: compensationTypeRef.current, validationInfo: compensationTypeState.validationInfo, setState: setCompensationTypeState },
    ]);
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.Reclamation, AnalyticsService.ErrorAction, e);
    });
  }

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());

  // === EVENT HANDLERS ===
  async function handleSubmitClick() {
    let isFormValid: boolean | undefined;
    let invalidValidationInfos: ValidationInfo[];
    let submitResult: { status: Status; errorCode: number | undefined } | undefined;
    await flushSync(async () => {
      if (submitStatus === Status.loading) return;

      setSubmitStatus(Status.notperformed);
      setSubmitErrorCode(undefined);

      if (!honeyPotRef.current.isValid()) return;

      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(AnalyticsService.Reclamation, AnalyticsService.ErrorAction, e);
        });
        return;
      }

      setSubmitStatus(Status.loading);
      const application: IReclamationApplication = {
        isRelatedToJourney: showActualTrip,
        actualTripFromGid: actualTripSelectorState.value.fromLocation?.location?.gid,
        actualTripFromName: actualTripSelectorState.value.fromLocation?.location?.name,
        actualTripFromDateTime: actualTripSelectorState.value.fromDateTime,
        actualTripToGid: actualTripSelectorState.value.toLocation?.location?.gid,
        actualTripToName: actualTripSelectorState.value.toLocation?.location?.name,
        actualTripToDateTime: actualTripSelectorState.value.toDateTime,
        reclamationComments: descriptionState.value,
        personalIdentityNumber: contactInformationLookupState.value.personalIdentityNumber,
        phoneNumber: contactInformationLookupState.value.phoneNumber,
        email: contactInformationLookupState.value.email,
        compensationType: compensationTypeState.value,
        isConsentGiven: consentState.value,
        formCreatedDateTime: DateTime.now(),
      };

      const formData = new FormData();
      formData.append('reclamationInfo', JSON.stringify(application));
      fileUploadState.value.forEach((file, index) => {
        formData.append(`files[${index}]`, file.actualFile);
      });

      try {
        const response = await fetch(`/api/reclamation?language=${LanguageService.getCurrentLanguage()}`, {
          method: 'POST',
          body: formData,
        });
        if (response.ok) {
          setSubmitStatus(Status.success);
          submitResult = { status: Status.success, errorCode: undefined };
          AnalyticsService.addEvent(AnalyticsService.Reclamation, 'Skickat reklamation', '');
        } else {
          setSubmitStatus(Status.failed);
          setSubmitErrorCode(response.status);
          submitResult = { status: Status.failed, errorCode: response.status };
          if (response.status === 400) {
            handleServerValidationErrors((await response.json()).errors);
          }
          AnalyticsService.addEvent(AnalyticsService.Reclamation, 'Skickat reklamation - misslyckat', '');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        setSubmitStatus(Status.failed);
        submitResult = { status: Status.failed, errorCode: (error as { status: number }).status };
      }
    });

    document.getElementById('reclamation-form')?.scrollIntoView();
    document.getElementById(!isFormValid || submitResult?.errorCode === 400 ? 'reclamation-form-validation-summary' : 'reclamation-form-submit-result')?.focus();
  }

  return (
    <div id="reclamation-form" className="reclamation-form">
      <div hidden={!(submitStatus !== Status.success && (submitStatus !== Status.failed || submitErrorCode === 400))}>
        {TranslationService.get('reclamationpage.textbody') && <div className="mb-4 mb-md-5">{TranslationService.get('reclamationpage.textbody')}</div>}
        <div className="mb-3 border-sm p-sm-3">
          {TranslationService.get('common.formsrequiredfieldsmessage') && <p className="mb-3">{TranslationService.get('common.formsrequiredfieldsmessage')}</p>}
          <ValidationSummary id="reclamation-form-validation-summary" validationSummaryErrors={validationSummaryErrors} />
          <TextFieldFormGroup
            ref={descriptionRef}
            renderAsTextArea={true}
            labelText={TranslationService.get('reclamationpage.commentsheading')}
            required={true}
            inputId="reclamation-comments"
            serverValidationName="reclamationComments"
            autocomplete="off"
            state={descriptionState}
            onChange={(value) => {
              setDescriptionState((ps) => ({ ...ps, value }));
            }}
            onKeyUp={() => {
              setDescriptionState((ps) => ({ ...ps, validationInfo: new ValidationInfo() }));
            }}
          />
          <hr className="my-4" />
          <div className="form-check mb-4">
            <input type="checkbox" checked={showActualTrip} onChange={(event) => setShowActualTrip(event.target.checked)} className="form-check-input" id="request-fromto-checkbox" />
            <label className="form-check-label fw-normal" htmlFor="request-fromto-checkbox">
              {TranslationService.get('reclamationpage.reclamationfromtoheading')}
            </label>
          </div>
          <div hidden={!showActualTrip}>
            <ActualTripSelector
              id="reclamation-actual-trip-selector"
              ref={actualTripSelectorRef}
              minDateTime={DateTime.now().startOf('day').minus({ days: 60 })}
              maxDateTime={DateTime.now().endOf('day').plus({ days: 60 })}
              showComments={false}
              state={actualTripSelectorState}
              onChange={(value) => setActualTripSelectorState((ps) => ({ ...ps, value }))}
              onKeyUp={(e) => {
                if (e === ActualTripSelectorField.fromLocation) {
                  setActualTripSelectorState((ps) => ({
                    ...ps,
                    validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, fromLocation: new ValidationInfo() }),
                  }));
                } else if (e === ActualTripSelectorField.fromDateTime) {
                  setActualTripSelectorState((ps) => ({
                    ...ps,
                    validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, fromDateTime: new ValidationInfo() }),
                  }));
                } else if (e === ActualTripSelectorField.toLocation) {
                  setActualTripSelectorState((ps) => ({
                    ...ps,
                    validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, toLocation: new ValidationInfo() }),
                  }));
                } else if (e === ActualTripSelectorField.toDateTime) {
                  setActualTripSelectorState((ps) => ({
                    ...ps,
                    validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, toDateTime: new ValidationInfo() }),
                  }));
                } else if (e === ActualTripSelectorField.comments) {
                  setActualTripSelectorState((ps) => ({
                    ...ps,
                    validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, comments: new ValidationInfo() }),
                  }));
                }
              }}
            />
          </div>
          <hr className="my-4" />
          <FileUpload
            id="reclamation-file-upload"
            ref={fileUploadRef}
            state={fileUploadState}
            onFilesAdded={(files) => {
              setFileUploadState((ps) => ({ ...ps, value: [...ps.value, ...files] }));
            }}
            onFileRemoved={(file) => {
              setFileUploadState((ps) => ({ ...ps, value: ps.value.filter((f) => f !== file), validationInfo: new ValidationInfo() }));
            }}
          />
          <hr className="my-4" />
          <ContactInformationLookup
            ref={contactInformationLookupRef}
            state={contactInformationLookupState}
            onChange={(value) => {
              setContactInformationLookupState((ps) => ({ ...ps, value }));
            }}
            onKeyUp={(e) => {
              if (e === ContactInformationLookupField.personalIdentityNumber) {
                setContactInformationLookupState((ps) => ({
                  ...ps,
                  validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, { ...ps.validationInfo.children, personalIdentityNumber: new ValidationInfo() }),
                }));
              } else if (e === ContactInformationLookupField.phoneNumber) {
                setContactInformationLookupState((ps) => ({
                  ...ps,
                  validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                    ...ps.validationInfo.children,
                    phoneNumber: new ValidationInfo(),
                  }),
                }));
              } else if (e === ContactInformationLookupField.email) {
                setContactInformationLookupState((ps) => ({
                  ...ps,
                  validationInfo: new ValidationInfo(ps.validationInfo.prefix, ps.validationInfo.errors, {
                    ...ps.validationInfo.children,
                    email: new ValidationInfo(),
                  }),
                }));
              }
            }}
          />
          {contactInformationLookupState.value.personalIdentityNumber && (
            <div>
              <hr className="my-4" />
              <CompensationTypeSelector
                ref={compensationTypeRef}
                availableCompensationTypes={[CompensationType.ToGoVoucherByEmail, CompensationType.VoucherByLetter]}
                state={compensationTypeState}
                onChange={(compensationType) => setCompensationTypeState((ps) => ({ ...ps, value: compensationType }))}
              />
              <hr className="my-4" />
              <Consent
                id="reclamation-consent"
                ref={consentRef}
                consentText={TranslationService.get('customerserviceforms.consenttext')}
                integrityPolicyInfo={TranslationService.get('customerserviceforms.integritypolicyinfo')}
                termsAndConditions={TranslationService.get('customerserviceforms.termsandconditions.heading')}
                state={consentState}
                onChange={(value) => {
                  setConsentState({ value, validationInfo: new ValidationInfo() });
                }}
              />
              <hr className="my-4" />
              <HoneyPot ref={honeyPotRef} />
              <div className="d-grid">
                <button type="submit" className={`btn btn-primary ${submitStatus === Status.loading ? 'btn-spinner' : ''}`} onClick={handleSubmitClick}>
                  <span hidden={submitStatus === Status.loading}>{TranslationService.get('reclamationpage.submitapplication')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div id="reclamation-form-submit-result" role="alert" aria-live="polite">
        {submitStatus === Status.failed && submitErrorCode !== 400 && (
          <>
            <SubmitFailed failedText={TranslationService.get('reclamationpage.failedsubmissiontext')} />
            <button
              className="btn btn-primary d-block mx-auto"
              onClick={() => {
                setSubmitStatus(Status.notperformed);
              }}
            >
              {TranslationService.get('reclamationpage.dismissfailedsubmissiontext')}
            </button>
          </>
        )}
        {submitStatus === Status.success && <SubmitSuccessful successText={TranslationService.get('reclamationpage.completedtext')} />}
      </div>
    </div>
  );
}

export default Reclamation;
